import * as React from 'react';
import { RenderElementProps } from 'slate-react';

import { Box, Button, usePrimitives, Dropdown } from '@churni/styleguide';
import {
  useIsAnswerMode,
  usePage,
  QuestionNodesContext
} from '../../containers';
import { getAnswers, getQuestions, getPages } from '../../helpers';
import { ReactEditor, useEditor } from 'slate-react';

import { PageCTA } from '../hovering-menu/PageCTA';

/*
 * Renderer for the next action.
 */
export function BlockButtonPrimary(
  props: RenderElementProps
): React.ReactElement {
  const { children, element, attributes } = props;

  const isAnswerMode = useIsAnswerMode();

  if (isAnswerMode) {
    return (
      <BlockButtonPrimaryAnswer element={element} attributes={attributes}>
        {children}
      </BlockButtonPrimaryAnswer>
    );
  }

  return (
    <BlockButtonPrimaryEdit element={element} attributes={attributes}>
      {children}
    </BlockButtonPrimaryEdit>
  );
}

export function BlockButtonPrimaryEdit(
  props: RenderElementProps
): React.ReactElement {
  const { children, element, attributes } = props;

  const { button } = usePrimitives();

  return (
    <Box {...attributes} ml={[0, 3]} contentEditable={false}>
      <Dropdown
        position={'north'}
        toggle={
          <Box
            sx={{
              ...button.styles,
              ...button.variants['primary']
            }}
          >
            {children}
          </Box>
        }
      >
        <PageCTA element={element} />
      </Dropdown>
    </Box>
  );
}

function BlockButtonPrimaryAnswer(
  props: RenderElementProps
): React.ReactElement {
  const { children, element, attributes } = props;

  const editor = useEditor();
  const path = ReactEditor.findPath(editor, element);
  const questionsContext = React.useContext(QuestionNodesContext);

  const pages = getPages(editor);

  const { currentPage, next, onFinish, onCancel, setErrors } = usePage();
  const isLastStep = currentPage + 1 === pages.length;

  const onClick = () => {
    const questions = questionsContext.filter(q => q[1][0] === path[0]);
    const pageQuestions = getQuestions(editor, questions);
    const pageAnswers = getAnswers(editor, questions);

    const errors = Object.values(pageQuestions).reduce((acc, question) => {
      if (!question.required) {
        return acc;
      }

      if (question.type === 'multiple-choice') {
        if (Object.values(pageAnswers[question.id].answers).length > 0) {
          return acc;
        }
      }

      if (
        question.type === 'text' &&
        pageAnswers[question.id].answer.length > 0
      ) {
        return acc;
      }

      return {
        ...acc,
        [question.id]: { error: `Question ${question.title} is required` }
      };
    }, {});

    if (Object.values(errors).length > 0) {
      setErrors(errors);
      return;
    }

    if (element.action === 'redirect' && element.redirectURL) {
      if (element.newWindow) {
        window.open(element.redirectURL, '_blank');
      } else {
        window.location.href = element.redirectURL;
      }

      return;
    }

    if (element.action === 'abandon') {
      onCancel(element.status);
      return;
    }

    isLastStep ? onFinish() : next();
  };

  return (
    <Box {...attributes} contentEditable={false} ml={[0, 3]}>
      <Button
        className={'form-next-button'}
        variant={'primary'}
        onClick={onClick}
      >
        {children}
      </Button>
    </Box>
  );
}
